import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ConversationService } from './conversation.service';
import {
    NativeTwilioVoiceEvent,
    TwilioVoice,
} from '../plugins/twilio-voice.plugin';
import { environment } from 'src/environments/environment';

export type NativeVoiceCallStatus =
    | 'idle'
    | 'ringing'
    | 'connecting'
    | 'in-progress'
    | 'reconnecting'
    | 'completed';

export interface NativeVoiceCallState {
    incomingCall: any | null;
    activeCallSid: string | null;
    callStatus: NativeVoiceCallStatus;
}

@Injectable({
    providedIn: 'root',
})
export class NativeTwilioVoiceService {
    private syncing = false;
    private listenersReady = false;
    private listenerHandles: PluginListenerHandle[] = [];
    private readonly stateSubject = new BehaviorSubject<NativeVoiceCallState>({
        incomingCall: null,
        activeCallSid: null,
        callStatus: 'idle',
    });

    readonly state$ = this.stateSubject.asObservable();

    constructor(
        private conversationService: ConversationService,
        private afAuth: AngularFireAuth
    ) {
        this.initListeners();
    }

    isNativeAndroid(): boolean {
        return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    }

    getSnapshot(): NativeVoiceCallState {
        return this.stateSubject.value;
    }

    async syncRegistration(): Promise<void> {
        if (!this.isNativeAndroid() || this.syncing) {
            return;
        }

        this.syncing = true;
        try {
            await this.initListeners();
            await this.ensurePermissions();
            await this.configureSession();
            const tokenResp = await this.conversationService.getVoiceToken().toPromise();
            await TwilioVoice.register({
                accessToken: tokenResp.token,
                identity: tokenResp.identity,
                expiresIn: tokenResp.expires_in,
            });
        } catch (error) {
            console.error('Failed to register native Twilio voice device:', error);
        } finally {
            this.syncing = false;
        }
    }

    async unregister(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        try {
            await TwilioVoice.unregister();
            this.resetState();
        } catch (error) {
            console.error('Failed to unregister native Twilio voice device:', error);
        }
    }

    async answerIncomingCall(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await this.ensurePermissions();

        const current = this.stateSubject.value;
        this.stateSubject.next({
            ...current,
            activeCallSid:
                current.activeCallSid || current.incomingCall?.call_sid || current.incomingCall?.callSid || null,
            callStatus: 'connecting',
        });

        await TwilioVoice.answerIncomingCall();
    }

    async startOutgoingCall(options: {
        to: string;
        from: string;
        conversationId?: string | number | null;
        customerId?: string | number | null;
        dealerId?: string | number | null;
        agentId?: string | number | null;
        contactName?: string | null;
    }): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await this.ensurePermissions();

        const current = this.stateSubject.value;
        this.stateSubject.next({
            ...current,
            callStatus: 'connecting',
        });

        await TwilioVoice.startOutgoingCall(options);
    }

    async rejectIncomingCall(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await TwilioVoice.rejectIncomingCall();
        this.resetState('completed');
    }

    async disconnectActiveCall(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await TwilioVoice.disconnectActiveCall();
    }

    async ensurePermissions(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        await TwilioVoice.ensurePermissions();
    }

    private async configureSession(): Promise<void> {
        if (!this.isNativeAndroid()) {
            return;
        }

        const authToken = await this.afAuth.idToken
            .pipe(
                filter((token): token is string => !!token),
                take(1)
            )
            .toPromise();

        await TwilioVoice.configureSession({
            apiUrl: environment.api_url,
            appToken: environment.app_token,
            authToken,
        });
    }

    markAnswerRequested(metadata?: {
        callSid?: string | null;
        conversationId?: string | number | null;
    }): void {
        if (!this.isNativeAndroid()) {
            return;
        }

        const current = this.stateSubject.value;
        const mergedIncoming = {
            ...(current.incomingCall || {}),
            call_sid: metadata?.callSid || current.incomingCall?.call_sid || null,
            conversation_id:
                metadata?.conversationId || current.incomingCall?.conversation_id || null,
        };

        this.stateSubject.next({
            incomingCall: mergedIncoming,
            activeCallSid:
                metadata?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
            callStatus: 'connecting',
        });
    }

    private async initListeners(): Promise<void> {
        if (!this.isNativeAndroid() || this.listenersReady) {
            return;
        }

        this.listenersReady = true;

        this.listenerHandles = await Promise.all([
            TwilioVoice.addListener('callInvite', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    ...current,
                    incomingCall: this.normalizeIncomingCall(event, current.incomingCall),
                    activeCallSid: current.activeCallSid,
                    callStatus: 'ringing',
                });
            }),
            TwilioVoice.addListener('callConnecting', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid:
                        event?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
                    callStatus: 'connecting',
                });
            }),
            TwilioVoice.addListener('callRinging', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid:
                        event?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
                    callStatus: 'connecting',
                });
            }),
            TwilioVoice.addListener('callConnected', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid:
                        event?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
                    callStatus: 'in-progress',
                });
            }),
            TwilioVoice.addListener('callReconnecting', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid:
                        event?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
                    callStatus: 'reconnecting',
                });
            }),
            TwilioVoice.addListener('callReconnected', (event) => {
                const current = this.stateSubject.value;
                this.stateSubject.next({
                    incomingCall: current.incomingCall,
                    activeCallSid:
                        event?.callSid || current.activeCallSid || current.incomingCall?.call_sid || null,
                    callStatus: 'in-progress',
                });
            }),
            TwilioVoice.addListener('callDisconnected', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('callCancelled', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('callConnectFailure', () => {
                this.resetState('completed');
            }),
            TwilioVoice.addListener('registrationError', (event) => {
                console.error('Native Twilio registration error:', event?.message || event);
            }),
        ]);
    }

    private normalizeIncomingCall(
        event: NativeTwilioVoiceEvent,
        existing: any | null
    ): any {
        return {
            ...(existing || {}),
            call_sid: event?.callSid || existing?.call_sid || null,
            conversation_id:
                event?.conversationId ?? existing?.conversation_id ?? null,
            from_number: event?.from || existing?.from_number || '',
            caller_name: event?.callerName || existing?.caller_name || 'Incoming caller',
            dealer_id: event?.dealerId ?? existing?.dealer_id ?? null,
            to_number: event?.toNumber || existing?.to_number || null,
        };
    }

    private resetState(callStatus: NativeVoiceCallStatus = 'idle'): void {
        this.stateSubject.next({
            incomingCall: null,
            activeCallSid: null,
            callStatus,
        });
    }
}
